import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, User, Bell, Palette, Edit, Trash2, MapPin } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import UserPreferences from '../components/UserPreferences';

const Profile = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [signups, setSignups] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [discordSignups, setDiscordSignups] = useState([]);
  const [showDiscordLink, setShowDiscordLink] = useState(false);
  const [discordUserId, setDiscordUserId] = useState('');
  const [formData, setFormData] = useState({
    notifications: user?.notifications ?? false,
    theme: user?.theme || 'dark',
    preferredUsername: user?.preferredUsername || user?.username || '',
    showCountdown: false
  });



  useEffect(() => {
    fetchUserSignups();
    fetchUserPreferences();
    checkDiscordSignups();
  }, []);

  // Update formData when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        notifications: user.notifications ?? false,
        theme: user.theme || 'dark',
        preferredUsername: user.preferredUsername || user.username || '',
        showCountdown: user.showCountdown ?? false
      });
    }
  }, [user]);

  const fetchUserSignups = async () => {
    try {
      const response = await axios.get('/api/events/user/signups');
      setSignups(response.data);
    } catch (error) {
      console.error('Failed to fetch user signups:', error);
    }
  };

  const fetchUserPreferences = async () => {
    try {
      const response = await axios.get('/api/users/preferences');
      setFormData(prev => ({
        ...prev,
        showCountdown: response.data.showCountdown ?? false
      }));
    } catch (error) {
      console.error('Failed to fetch user preferences:', error);
    }
  };

  const checkDiscordSignups = async () => {
    try {
      const response = await axios.get('/api/users/check-discord-signups');
      if (response.data.discordSignups && response.data.discordSignups.length > 0) {
        setDiscordSignups(response.data.discordSignups);
        setShowDiscordLink(true);
      }
    } catch (error) {
      console.error('Failed to check Discord signups:', error);
    }
  };

  const handleLinkDiscordAccount = async () => {
    if (!discordUserId.trim()) {
      alert('Please enter your Discord User ID');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/api/users/link-discord-account', {
        discordUserId: discordUserId.trim()
      });
      
      alert(`Discord account linked successfully! ${response.data.linkedSignups} signups were linked.`);
      setShowDiscordLink(false);
      setDiscordUserId('');
      
      // Refresh signups to show the linked ones
      fetchUserSignups();
      checkDiscordSignups();
    } catch (error) {
      console.error('Failed to link Discord account:', error);
      alert(error.response?.data?.error || 'Failed to link Discord account');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Update profile preferences
      const profileResponse = await axios.put('/api/users/profile', {
        notifications: formData.notifications,
        theme: formData.theme
      });
      
      // Update user preferences (including showCountdown)
      await axios.put('/api/users/preferences', {
        preferredPlayers: [],
        preferredStorytellers: [],
        notes: '',
        showCountdown: formData.showCountdown
      });
      
      // Update preferred username if it changed
      if (formData.preferredUsername !== user?.preferredUsername) {
        const usernameResponse = await axios.put('/api/auth/update-username', {
          preferredUsername: formData.preferredUsername
        });
        // Merge the responses
        updateUser({ ...profileResponse.data, ...usernameResponse.data.user });
      } else {
        updateUser(profileResponse.data);
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      if (error.response?.data?.error) {
        alert(`Failed to update profile: ${error.response.data.error}`);
      } else {
        alert('Failed to update profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm('⚠️ WARNING: This will permanently delete your profile and all associated data. This action cannot be undone. Are you sure you want to continue?')) {
      return;
    }

    if (!window.confirm('⚠️ FINAL WARNING: This will delete your profile, signups, preferences, and all references to you. Type "DELETE" to confirm:')) {
      return;
    }

    const confirmation = prompt('Type "DELETE" to confirm:');
    if (confirmation !== 'DELETE') {
      alert('Profile deletion cancelled.');
      return;
    }

    try {
      await axios.delete('/api/users/profile');
      alert('Profile deleted successfully. You will be logged out.');
      logout();
      setTimeout(() => {
        navigate('/login');
      }, 1000);
    } catch (error) {
      console.error('Failed to delete profile:', error);
      alert('Failed to delete profile. Please try again.');
    }
  };



  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'confirmed': return 'badge-confirmed';
      case 'tentative': return 'badge-tentative';
      case 'late': return 'badge-late';
      case 'declined': return 'badge-declined';
      default: return 'badge-secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed': return <User size={16} className="text-green-600" />;
      case 'tentative': return <Bell size={16} className="text-orange-600" />;
      case 'late': return <Bell size={16} className="text-yellow-600" />;
      case 'declined': return <X size={16} className="text-red-600" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-8">
      {/* Profile Header */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-primary">Profile</h1>
          <div className="flex space-x-2">
            <button
              onClick={() => setShowPreferences(true)}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <User size={16} />
              <span>Player Preferences</span>
            </button>
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="btn btn-secondary flex items-center space-x-2"
              >
                <Edit size={16} />
                <span>Edit Profile</span>
              </button>
            ) : (
              <div className="flex space-x-2">
                <button
                  onClick={() => setIsEditing(false)}
                  className="btn btn-secondary flex items-center space-x-2"
                >
                  <X size={16} />
                  <span>Cancel</span>
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Save size={16} />
                  <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* User Info */}
          <div>
            <h2 className="text-lg font-semibold text-primary mb-4">User Information</h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                {user?.avatar && (
                  <img
                    src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png`}
                    alt={user.username}
                    className="w-16 h-16 rounded-full"
                  />
                )}
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-primary">
                    {user?.preferredUsername || user?.username}
                  </h3>
                  <p className="text-secondary">Discord ID: {user?.discordId || 'Not linked'}</p>
                  <p className="text-secondary capitalize">Role: {user?.role}</p>
                  
                  {/* Discord Account Linking */}
                  {showDiscordLink && (
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                        🔗 Link Discord Account
                      </h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">
                        We found {discordSignups.length} Discord signup{discordSignups.length !== 1 ? 's' : ''} that might be yours. 
                        Link your Discord account to claim them.
                      </p>
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={discordUserId}
                          onChange={(e) => setDiscordUserId(e.target.value)}
                          placeholder="Enter your Discord User ID"
                          className="input input-sm w-full"
                        />
                        <button
                          onClick={handleLinkDiscordAccount}
                          disabled={loading}
                          className="btn btn-primary btn-sm w-full"
                        >
                          {loading ? 'Linking...' : 'Link Discord Account'}
                        </button>
                      </div>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                        💡 To find your Discord User ID, enable Developer Mode in Discord, 
                        right-click your username, and select "Copy User ID"
                      </p>
                    </div>
                  )}
                  
                  {/* Preferred Username */}
                  <div className="mt-3">
                    {isEditing ? (
                      <div>
                        <label htmlFor="preferredUsername" className="block text-sm font-medium text-secondary mb-1">
                          Preferred Username
                        </label>
                        <input
                          id="preferredUsername"
                          name="preferredUsername"
                          type="text"
                          value={formData.preferredUsername}
                          onChange={handleInputChange}
                          className="input w-full"
                          placeholder="Enter your preferred username"
                          maxLength={255}
                        />
                        <p className="text-xs text-secondary mt-1">
                          This will be displayed instead of your Discord username
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-sm text-secondary">Preferred Username</p>
                        <p className="text-primary">
                          {user?.preferredUsername || user?.username || 'Not set'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Preferences */}
          <div>
            <h2 className="text-lg font-semibold text-primary mb-4">Preferences</h2>
            <div className="space-y-4">
              {isEditing ? (
                <>
                  <div className="flex items-center">
                    <input
                      id="notifications"
                      name="notifications"
                      type="checkbox"
                      checked={formData.notifications}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="notifications" className="ml-2 block text-sm text-secondary">
                      Enable notifications
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="showCountdown"
                      name="showCountdown"
                      type="checkbox"
                      checked={formData.showCountdown}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label htmlFor="showCountdown" className="ml-2 block text-sm text-secondary">
                      Show countdown on event pages
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-sm text-secondary">Notifications</p>
                    <p className="text-primary">{formData.notifications ? 'Enabled' : 'Disabled'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-secondary">Show Countdown</p>
                    <p className="text-primary">{formData.showCountdown ? 'Enabled' : 'Disabled'}</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Signup History */}
      <div className="card">
        <h2 className="text-lg font-semibold text-primary mb-6">Signup History</h2>
        
        {signups.length === 0 ? (
          <div className="text-center py-8">
            <User className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-primary">No signups yet</h3>
            <p className="mt-1 text-sm text-muted">
              You haven't signed up for any events yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {signups.map((signup) => (
              <div
                key={signup.id}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {getStatusIcon(signup.status)}
                      <h3 className="text-lg font-medium text-primary">
                        <a 
                          href={`/events/${signup.event_id}`}
                          className="hover:text-primary-600 hover:underline cursor-pointer"
                        >
                          {signup.title}
                        </a>
                      </h3>
                      <span className={`badge ${getStatusBadgeClass(signup.status)}`}>
                        {signup.status}
                      </span>
                      {signup.signup_source && signup.signup_source !== 'webapp' && (
                        <span className="badge badge-secondary text-xs">
                          {signup.signup_source === 'discord' ? 'Discord' : signup.signup_source}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-secondary mb-2">
                      <div className="flex items-center space-x-1">
                        <User size={14} />
                        <span>{new Date(signup.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Bell size={14} />
                        <span>{new Date(signup.date).toLocaleTimeString()}</span>
                      </div>
                      {signup.location && (
                        <div className="flex items-center space-x-1">
                          <MapPin size={14} />
                          <span>{signup.location}</span>
                        </div>
                      )}
                    </div>
                    
                    {signup.notes && (
                      <p className="text-sm text-secondary mt-2">
                        <strong>Notes:</strong> {signup.notes}
                      </p>
                    )}
                  </div>
                  
                  <div className="ml-4">
                    <button
                      onClick={() => window.location.href = `/events/${signup.event_id}`}
                      className="btn btn-secondary text-sm"
                    >
                      View Event
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Account Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 dark:bg-primary-900 rounded-lg">
              <User className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary">Total Signups</p>
              <p className="text-2xl font-bold text-primary">{signups.length}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <User className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary">Confirmed</p>
              <p className="text-2xl font-bold text-primary">
                {signups.filter(s => s.status === 'confirmed').length}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
              <Bell className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-secondary">Tentative</p>
              <p className="text-2xl font-bold text-primary">
                {signups.filter(s => s.status === 'tentative').length}
              </p>
            </div>
          </div>
        </div>
      </div>



      {/* Delete Profile Section */}
      <div className="card border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">Danger Zone</h2>
            <p className="text-sm text-red-700 dark:text-red-300">
              Once you delete your profile, there is no going back. All your data including signups, preferences, and event history will be permanently removed.
            </p>
          </div>
          <button
            onClick={handleDeleteProfile}
            disabled={loading}
            className="btn btn-danger flex items-center space-x-2"
          >
            <Trash2 size={16} />
            <span>{loading ? 'Deleting...' : 'Delete Profile'}</span>
          </button>
        </div>
      </div>

      {/* User Preferences Modal */}
      {showPreferences && (
        <UserPreferences onClose={() => setShowPreferences(false)} />
      )}
    </div>
  );
};

export default Profile; 